import { SnackbarAriaLive, SnackbarContext, SnackbarVariant } from './snackbar-config';
import { HxSnackbarRef } from './snackbar-ref';

/**
 * Internal data structure passed to HxSnackbar component. Created by service/container from HxSnackbarConfig.
 *
 * @internal
 */
export type HxSnackbarData = {
  /** Notification message text (already validated as non-empty) */
  message: string;

  /** Visual variant (default resolved) */
  variant: SnackbarVariant;

  /** Semantic context (default resolved) */
  context: SnackbarContext;

  /** Show context-specific icon */
  hasIcon: boolean;

  /** Icon CSS class (e.g., 'cc-icon-check') Computed from context when hasIcon is true */
  icon?: string;

  /** Show action button */
  hasAction: boolean;

  /** Action button label (validated if hasAction is true) */
  actionLabel?: string;

  /** Action button click handler */
  onAction?: () => void | Promise<void>;

  /** Show close button */
  hasClose: boolean;

  /** Reference to this snackbar instance Allows component to call dismiss() */
  snackbarRef: HxSnackbarRef;

  /** Additional CSS class for custom styling */
  customClass?: string;

  /** ARIA live region politeness (auto-selected based on context) */
  ariaLive: SnackbarAriaLive;

  /** Screen reader announcement message Falls back to message if not provided */
  announcementMessage: string;
};

/** Icon mapping for context-specific icons. Maps SnackbarContext to icon CSS class. */
export const SNACKBAR_ICON_MAP: Record<SnackbarContext, string> = {
  positive: 'ccs-check-circle', // Checkmark/success icon
  negative: 'ccs-x-circle', // X/error icon
  caution: 'ccs-exclamation-circle', // Warning triangle icon
  info: 'ccs-info-circle', // Information circle icon
  neutral: 'ccs-bell', // Generic notification bell icon
  brand: 'ccs-bell', // Generic notification bell icon (brandable)
};

/**
 * Get icon CSS class for given context. Returns undefined if hasIcon is false.
 *
 * @param context - Snackbar context
 * @param hasIcon - Whether to show icon
 * @returns Icon CSS class or undefined
 */
export function getSnackbarIcon(context: SnackbarContext, hasIcon: boolean): string | undefined {
  return hasIcon ? SNACKBAR_ICON_MAP[context] : undefined;
}

/**
 * Get ARIA role for given context. Critical contexts (negative, caution) use 'alert'. Non-critical contexts use
 * 'status'.
 *
 * @param context - Snackbar context
 * @returns ARIA role attribute value
 */
export function getAriaRole(context: SnackbarContext): 'status' | 'alert' {
  if (context === 'negative' || context === 'caution') {
    return 'alert';
  }
  return 'status';
}

/**
 * Get ARIA live politeness for given context. Critical contexts (negative, caution) default to 'assertive'.
 * Non-critical contexts default to 'polite'.
 *
 * @param context - Snackbar context
 * @param configPoliteness - Explicit config override
 * @returns ARIA live politeness value
 */
export function getAriaPoliteness(context: SnackbarContext, configPoliteness?: SnackbarAriaLive): SnackbarAriaLive {
  // Explicit config overrides default
  if (configPoliteness) {
    return configPoliteness;
  }

  // Critical contexts are assertive (interrupt screen reader)
  if (context === 'negative' || context === 'caution') {
    return 'assertive';
  }

  // Non-critical contexts are polite (wait for pause)
  return 'polite';
}
